import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';
import { Sun as SunIcon, Moon as MoonIcon } from '@phosphor-icons/react/dist/ssr';
import { useMediaQuery, useTheme } from '@mui/material';

import { paths } from '@/paths';
import { isNavItemActive } from '@/lib/is-nav-item-active';
import { usePathname } from '@/hooks/use-pathname';
import { useSettings } from '@/hooks/use-settings';
import { setSettings as setPersistedSettings } from '@/lib/settings/set-settings';
import { useColorScheme } from '@mui/material/styles';
import { RouterLink } from '@/components/core/link';
import { Logo } from '@/components/core/logo';
import { logger } from '@/lib/default-logger';
import DataContext from '@/contexts/DataContext';

import { icons } from '../nav-icons';
import { ProjectsSwitch } from '../projects-switch';
import { navColorStyles } from './styles';


const logoColors = {
  dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
  light: { blend_in: 'dark', discrete: 'dark', evident: 'light' },
};

function NavItem({
  children,
  id,
  depth,
  disabled,
  external,
  forceOpen = false,
  href,
  icon,
  label,
  matcher,
  pathname,
  name,
  onClick = () => {}
}) {
  const [open, setOpen] = React.useState(forceOpen);
  const active = isNavItemActive({ disabled, external, href, matcher, pathname });
  const Icon = icon ? icons[icon] : null;
  const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
  const isBranch = children && !href;
  const showChildren = Boolean(children && open);

  return (
    <Box component="li" data-depth={depth} sx={{ userSelect: 'none' }} key={id || `nav-item-${Math.random() * 10}`} onClick={onClick}>
      <Box
        {...(isBranch
          ? {
              onClick: () => {
                setOpen(!open);
              },
              onKeyUp: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setOpen(!open);
                }
              },
              role: 'button',
            }
          : {
              ...(href
                ? {
                    component: external ? 'a' : RouterLink,
                    href,
                    target: external ? '_blank' : undefined,
                    rel: external ? 'noreferrer' : undefined,
                  }
                : { role: 'button' }),
            })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--NavItem-color)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          position: 'relative',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && {
            bgcolor: 'var(--NavItem-disabled-background)',
            color: 'var(--NavItem-disabled-color)',
            cursor: 'not-allowed',
          }),
          ...(active && {
            bgcolor: 'var(--NavItem-active-background)',
            color: 'var(--NavItem-active-color)',
            ...(depth > 0 && {
              '&::before': {
                bgcolor: 'var(--NavItem-children-indicator)',
                borderRadius: '2px',
                content: '" "',
                height: '20px',
                left: '-14px',
                position: 'absolute',
                width: '3px',
              },
            }),
          }),
          ...(open && { color: 'var(--NavItem-open-color)' }),
          '&:hover': {
            ...(!disabled && !active && { 
              bgcolor: 'var(--NavItem-hover-background)', 
              color: 'var(--NavItem-hover-color)' 
            }),
          },
        }}
        tabIndex={0}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
          {Icon ? (
            <Icon
              fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
              fontSize="var(--icon-fontSize-md)"
              weight={forceOpen || active ? 'fill' : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {name}
          </Typography>
        </Box>
        {label ? <Chip color="primary" label={label} size="small" /> : null}
        {external ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
        {isBranch ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
      </Box>
      {showChildren ? (
        <Box sx={{ pl: '24px' }}>
          <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
        </Box>
      ) : null}
    </Box>
  );
}

function renderNavGroups({ items, pathname }) {
  return (
    <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {/* {items.map((group) => (
        <Stack component="li" key={group.id || group.name} spacing={1.5}> */}
      {items.map((group, index) => (
        <Stack component="li" key={index} spacing={1.5}>
          {group.name && (
            <Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
              {group.name}
            </Typography>
          )}
          {renderNavItems({ depth: 0, items: group.items, pathname })}
        </Stack>
      ))}
    </Stack>
  );
}


function renderNavItems({ depth = 0, items = [], pathname }) {
  const {setCurrentView, setSelectedViewParams} = React.useContext(DataContext)
  function onNavItemClick() {
  // logger.debug('nav item click')
  // setCurrentView(null);
  // setSelectedViewParams({});
}
  return (
    <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((item) => {
        // logger.debug('nav item', item)
        const { items: childItems, id, ...rest } = item;
        const key = id || `nav-item-${Math.random() * 10}`;
        const forceOpen = childItems
          ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
          : false;

        return (
          <NavItem depth={depth} forceOpen={forceOpen} pathname={pathname} id={key} {...rest} key={key} onClick={onNavItemClick}>
            {childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems }) : null}
          </NavItem>
        );
      })}
    </Stack>
  );
}

export function SideNav({ color = 'evident', items = [], onMobileClose, open }) {
  const pathname = usePathname();
  
  const {settings, setSettings} = useSettings();
  const { setColorScheme } = useColorScheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  React.useEffect(() => {
    // logger.debug('[SideNav] Mobile state changed:', { isMobile, open });
  }, [isMobile, open]);

  const handleDrawerClose = React.useCallback(() => {
    // logger.debug('[SideNav] Drawer closing');
    onMobileClose?.();
  }, [onMobileClose]);

  const handleToggleTheme = React.useCallback(() => {
    const newScheme = settings.colorScheme === 'dark' ? 'light' : 'dark';
    // logger.debug('[SideNav] Theme toggle:', { current: settings.colorScheme, new: newScheme });
    setColorScheme(newScheme);

    const updatedSettings = { ...settings, colorScheme: newScheme};
    setPersistedSettings(updatedSettings);
    setSettings(updatedSettings);

  }, [settings.colorScheme, setColorScheme, setSettings]);

  const styles = navColorStyles[settings.colorScheme][color];
  const logoColor = logoColors[settings.colorScheme][color];

  const sidebarContent = (
    <Box
      sx={{
        ...styles,
        bgcolor: 'var(--SideNav-background)',
        borderRight: 'var(--SideNav-border)',
        color: 'var(--SideNav-color)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Logo and Project Switch */}
      <Stack spacing={2} sx={{ p: 2 }}>
        <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-flex' }}>
          {/* <Logo color={logoColor} height={32} width={122} /> */}
          <Logo color={logoColor} height={40} width={152.5} />
        </Box>
        <ProjectsSwitch />
      </Stack>

      {/* Navigation Items */}
      <Box
        component="nav"
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          pb: '64px', // Add padding to prevent content being hidden behind theme toggle
        }}
      >
        {renderNavGroups({ items, pathname })}
      </Box>

      {/* Theme Toggle */}
      <Box
        sx={{
          borderTop: '1px solid var(--NavItem-children-border)',
          mt: 'auto',
          p: 1,
          // backgroundColor: 'var(--SideNav-background)',
          backgroundColor: 'inherit',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Tooltip title={`Switch to ${settings.colorScheme === 'dark' ? 'light' : 'dark'} mode`}>
          <IconButton 
            onClick={handleToggleTheme}
            sx={{ 
              width: '100%',
              height: '44px',
              color: 'inherit',
            }}
          >
            {settings.colorScheme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 'var(--SideNav-width)',
              height: '100%',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            width: 'var(--SideNav-width)',
            flexShrink: 0,
            position: 'fixed',
            height: '100%',
            left: 0,
            top: 0,
            zIndex: 'var(--SideNav-zIndex)',
          }}
        >
          {sidebarContent}
        </Box>
      )}
    </>
  );
}











// import * as React from 'react';
// import Box from '@mui/material/Box';
// import Chip from '@mui/material/Chip';
// import Stack from '@mui/material/Stack';
// import Typography from '@mui/material/Typography';
// import IconButton from '@mui/material/IconButton';
// import Tooltip from '@mui/material/Tooltip';
// import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
// import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
// import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';
// import { Sun as SunIcon, Moon as MoonIcon } from '@phosphor-icons/react/dist/ssr';

// import { paths } from '@/paths';
// import { isNavItemActive } from '@/lib/is-nav-item-active';
// import { usePathname } from '@/hooks/use-pathname';
// import { useSettings } from '@/hooks/use-settings';
// import { useColorScheme } from '@mui/material/styles';
// import { RouterLink } from '@/components/core/link';
// import { Logo } from '@/components/core/logo';

// import { icons } from '../nav-icons';
// import { ProjectsSwitch } from '../projects-switch';
// import { navColorStyles } from './styles';

// const logoColors = {
//   dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
//   light: { blend_in: 'dark', discrete: 'dark', evident: 'light' },
// };

// export function SideNav({ color = 'evident', items = [] }) {
//   const pathname = usePathname();
//   const {
//     settings: { colorScheme = 'light', primaryColor = '#1976d2', secondaryColor = '#9c27b0' },
//     setSettings,
//   } = useSettings();
//   const { setColorScheme } = useColorScheme();

//   // Toggle theme function
//   const handleToggleTheme = () => {
//     const newScheme = colorScheme === 'dark' ? 'light' : 'dark';
//     setColorScheme(newScheme);
//     setSettings((prevSettings) => ({
//       ...prevSettings,
//       colorScheme: newScheme,
//     }));
//   };

//   // Change primary color
//   const handleSetPrimaryColor = (color) => {
//     setSettings((prevSettings) => ({
//       ...prevSettings,
//       primaryColor: color,
//     }));
//   };

//   // Change secondary color
//   const handleSetSecondaryColor = (color) => {
//     setSettings((prevSettings) => ({
//       ...prevSettings,
//       secondaryColor: color,
//     }));
//   };

//   const styles = navColorStyles[colorScheme][color];
//   const logoColor = logoColors[colorScheme][color];

//   return (
//     <Box
//       sx={{
//         ...styles,
//         bgcolor: 'var(--SideNav-background)',
//         borderRight: 'var(--SideNav-border)',
//         color: 'var(--SideNav-color)',
//         display: { xs: 'none', lg: 'flex' },
//         flexDirection: 'column',
//         height: '100%',
//         left: 0,
//         position: 'fixed',
//         top: 0,
//         width: 'var(--SideNav-width)',
//         zIndex: 'var(--SideNav-zIndex)',
//       }}
//     >
//       <Stack spacing={2} sx={{ p: 2 }}>
//         <div>
//           <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-flex' }}>
//             <Logo color={logoColor} height={32} width={122} />
//           </Box>
//         </div>
//         <ProjectsSwitch />
//       </Stack>
//       <Box
//         component="nav"
//         sx={{
//           flex: '1 1 auto',
//           overflowY: 'auto',
//           p: 2,
//           scrollbarWidth: 'none',
//           '&::-webkit-scrollbar': { display: 'none' },
//         }}
//       >
//         {renderNavGroups({ items, pathname })}
//       </Box>
//       {/* Add Light/Dark Mode Toggle Button */}
//       <Box sx={{ p: 2, mt: 'auto' }}>
//         <Tooltip title={`Switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}>
//           <IconButton onClick={handleToggleTheme} sx={{ color: 'inherit' }}>
//             {colorScheme === 'dark' ? <SunIcon /> : <MoonIcon />}
//           </IconButton>
//         </Tooltip>
//         {/* Add Primary Color Options */}
//         <Box sx={{ mt: 2 }}>
//           <Typography variant="subtitle2" sx={{ mb: 1 }}>
//             Primary Color
//           </Typography>
//           <Stack direction="row" spacing={1}>
//             <Chip
//               label="Blue"
//               onClick={() => handleSetPrimaryColor('#1976d2')}
//               sx={{ bgcolor: '#1976d2', color: '#fff', cursor: 'pointer' }}
//             />
//             <Chip
//               label="Red"
//               onClick={() => handleSetPrimaryColor('#d32f2f')}
//               sx={{ bgcolor: '#d32f2f', color: '#fff', cursor: 'pointer' }}
//             />
//             <Chip
//               label="Green"
//               onClick={() => handleSetPrimaryColor('#388e3c')}
//               sx={{ bgcolor: '#388e3c', color: '#fff', cursor: 'pointer' }}
//             />
//           </Stack>
//         </Box>
//         {/* Add Secondary Color Options */}
//         <Box sx={{ mt: 2 }}>
//           <Typography variant="subtitle2" sx={{ mb: 1 }}>
//             Secondary Color
//           </Typography>
//           <Stack direction="row" spacing={1}>
//             <Chip
//               label="Purple"
//               onClick={() => handleSetSecondaryColor('#9c27b0')}
//               sx={{ bgcolor: '#9c27b0', color: '#fff', cursor: 'pointer' }}
//             />
//             <Chip
//               label="Orange"
//               onClick={() => handleSetSecondaryColor('#f57c00')}
//               sx={{ bgcolor: '#f57c00', color: '#fff', cursor: 'pointer' }}
//             />
//             <Chip
//               label="Pink"
//               onClick={() => handleSetSecondaryColor('#e91e63')}
//               sx={{ bgcolor: '#e91e63', color: '#fff', cursor: 'pointer' }}
//             />
//           </Stack>
//         </Box>
//       </Box>
//     </Box>
//   );
// }

// function renderNavGroups({ items, pathname }) {
//   const children = items.reduce((acc, curr) => {
//     let key = curr.id ? `${curr.id}-nav-group-${Math.random() * 10}` : `nav-group-${Math.random() * 10}`;
//     acc.push(
//       <Stack component="li" key={key} spacing={1.5}>
//         {curr.name ? (
//           <div>
//             <Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
//               {curr.name}
//             </Typography>
//           </div>
//         ) : null}
//         <div>{renderNavItems({ depth: 0, items: curr.items, pathname })}</div>
//       </Stack>
//     );

//     return acc;
//   }, []);

//   return (
//     <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
//       {children}
//     </Stack>
//   );
// }

// function renderNavItems({ depth = 0, items = [], pathname }) {
//   const children = items.reduce((acc, curr) => {
//     const { items: childItems, id, ...item } = curr;
//     let key = id ? `${id}-nav-item-${Math.random() * 10}` : `nav-item-${Math.random() * 10}`;

//     const forceOpen = childItems
//       ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
//       : false;

//     acc.push(
//       <NavItem depth={depth} forceOpen={forceOpen} pathname={pathname} id={key} {...item} key={key}>
//         {childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems }) : null}
//       </NavItem>
//     );

//     return acc;
//   }, []);

//   return (
//     <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
//       {children}
//     </Stack>
//   );
// }

// function NavItem({
//   children,
//   id,
//   depth,
//   disabled,
//   external,
//   forceOpen = false,
//   href,
//   icon,
//   label,
//   matcher,
//   pathname,
//   name,
// }) {
//   const [open, setOpen] = React.useState(forceOpen);
//   const active = isNavItemActive({ disabled, external, href, matcher, pathname });
//   const Icon = icon ? icons[icon] : null;
//   const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
//   const isBranch = children && !href;
//   const showChildren = Boolean(children && open);

//   return (
//     <Box component="li" data-depth={depth} sx={{ userSelect: 'none' }} key={id || `nav-item-${Math.random() * 10}`}>
//       <Box
//         {...(isBranch
//           ? {
//               onClick: () => {
//                 setOpen(!open);
//               },
//               onKeyUp: (event) => {
//                 if (event.key === 'Enter' || event.key === ' ') {
//                   setOpen(!open);
//                 }
//               },
//               role: 'button',
//             }
//           : {
//               ...(href
//                 ? {
//                     component: external ? 'a' : RouterLink,
//                     href,
//                     target: external ? '_blank' : undefined,
//                     rel: external ? 'noreferrer' : undefined,
//                   }
//                 : { role: 'button' }),
//             })}
//         sx={{
//           alignItems: 'center',
//           borderRadius: 1,
//           color: 'var(--NavItem-color)',
//           cursor: 'pointer',
//           display: 'flex',
//           flex: '0 0 auto',
//           gap: 1,
//           p: '6px 16px',
//           position: 'relative',
//           textDecoration: 'none',
//           whiteSpace: 'nowrap',
//           ...(disabled && {
//             bgcolor: 'var(--NavItem-disabled-background)',
//             color: 'var(--NavItem-disabled-color)',
//             cursor: 'not-allowed',
//           }),
//           ...(active && {
//             bgcolor: 'var(--NavItem-active-background)',
//             color: 'var(--NavItem-active-color)',
//             ...(depth > 0 && {
//               '&::before': {
//                 bgcolor: 'var(--NavItem-children-indicator)',
//                 borderRadius: '2px',
//                 content: '" "',
//                 height: '20px',
//                 left: '-14px',
//                 position: 'absolute',
//                 width: '3px',
//               },
//             }),
//           }),
//           ...(open && { color: 'var(--NavItem-open-color)' }),
//           '&:hover': {
//             ...(!disabled &&
//               !active && { bgcolor: 'var(--NavItem-hover-background)', color: 'var(--NavItem-hover-color)' }),
//           },
//         }}
//         tabIndex={0}
//       >
//         <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
//           {Icon ? (
//             <Icon
//               fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
//               fontSize="var(--icon-fontSize-md)"
//               weight={forceOpen || active ? 'fill' : undefined}
//             />
//           ) : null}
//         </Box>
//         <Box sx={{ flex: '1 1 auto' }}>
//           <Typography
//             component="span"
//             sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
//           >
//             {name}
//           </Typography>
//         </Box>
//         {label ? <Chip color="primary" label={label} size="small" /> : null}
//         {external ? (
//           <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
//             <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
//           </Box>
//         ) : null}
//         {isBranch ? (
//           <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
//             <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
//           </Box>
//         ) : null}
//       </Box>
//       {showChildren ? (
//         <Box sx={{ pl: '24px' }}>
//           <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
//         </Box>
//       ) : null}
//     </Box>
//   );
// }